
import * as Yup from 'yup';
import { useMemo, useEffect } from 'react';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { Draggable, Droppable, DragDropContext } from '@hello-pangea/dnd';

import { Box } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Grid,
  Alert,
  Stack,
  Button,
  Dialog,
  MenuItem,
  IconButton,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent
} from '@mui/material';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { OrganizationSetting } from 'src/services/organisation/organisation.types';
import { ReferenceCollectionMethod } from 'src/services/references/references.types';
import { useCreateReferenceRequestMutation } from 'src/services/references/references.service';
import { useGetOrganizationSettingsQuery, useGetReferenceTemplateSettingsQuery } from 'src/services/organisation/organisation.service';

import Iconify from 'src/components/iconify';
import RHFNumberInput from 'src/components/hook-form/rhf-number-input';
import { RHFSelect, RHFCheckbox, RHFTextField } from 'src/components/hook-form';

type Props = {
  open: boolean;
  applicationId: string;
  onClose: VoidFunction;
};

interface FormValues {
  collection_method: ReferenceCollectionMethod;
  referee_count: number;
  questions: Array<{ question: string; required: boolean; id?: string, order: number }>;
  notes?: string | null;
}

const MAX_QUESTIONS = 15;
const MAX_REFEREES = 10;

export default function RequestReferenceModal({ open, applicationId, onClose }: Props) {

  const { t } = useTranslate();

  const tenantType = useOrgTenant();

  const authContext = useAuthContext();

  const dispatch = useAppDispatch();

  const { currentData: settingsData, isLoading: isLoadingSettings } = useGetOrganizationSettingsQuery(
    authContext.organization.id
  );

  const { currentData: templateQuestions, isLoading: isLoadingTemplateQuestions } = useGetReferenceTemplateSettingsQuery(authContext.organization.id);

  const [createRequest, { isLoading: isSubmitting }] = useCreateReferenceRequestMutation();

  const CreateRequestSchema = Yup.object().shape({
    referee_count: Yup.number().min(1, t('validation.min_integer', { min: 1 })).max(MAX_REFEREES, t('validation.max_integer', { max: MAX_REFEREES })).required(t('validation.required')),
    collection_method: Yup.string().oneOf([ReferenceCollectionMethod.REFEREE, ReferenceCollectionMethod.MANUAL]).required(t('validation.required')),
    questions: Yup.array().of(Yup.object().shape({
      question: Yup.string().required(t('validation.required')),
      required: Yup.boolean().required(t('validation.required')),
      order: Yup.number().required(),
      id: Yup.string().optional(),
    })).min(1, t('validation.array_min', { min: 1 })).max(MAX_QUESTIONS, t('validation.array_max', { max: MAX_QUESTIONS })).required(t('validation.required')),
    notes: Yup.string().nullable(),
  });

  // Ensure all questions have IDs for stable dragging
  const initialQuestions = useMemo(() => {
    if (Array.isArray(templateQuestions) && templateQuestions.length > 0) {
      return templateQuestions.map((item: any) => ({
        ...item,
        id: item.id || `question-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      }));
    }
    return [];
  }, [templateQuestions]);

  const defaultValues = {
    referee_count: 1,
    collection_method: ReferenceCollectionMethod.REFEREE,
    questions: initialQuestions,
    notes: null,
  };

  const methods = useForm<FormValues>({
    resolver: yupResolver(CreateRequestSchema),
    defaultValues,
    mode: 'all',
  });

  const {
    handleSubmit,
    reset,
    control,
    watch,
    getValues,
    formState: { isValid, errors },
  } = methods;

  const collectionMethod = watch('collection_method');

  useEffect(() => {
    if (Array.isArray(templateQuestions) && templateQuestions.length > 0) {
      // Ensure all questions have IDs for stable dragging
      const updatedQuestions = templateQuestions.map((item: any) => ({
        ...item,
        id: item.id || `question-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      }));

      reset({
        ...getValues(),
        questions: updatedQuestions
      });
    }
  }, [templateQuestions, reset, getValues]);

  const areQuestionsEditable = useMemo(() => {
    if (!settingsData) return false;

    const referenceQuestionSetting = settingsData.find(
      (setting) => setting.setting_type === OrganizationSetting.REFERENCE_TEMPLATE_QUESTIONS_EDITABLE
    );

    return referenceQuestionSetting ? referenceQuestionSetting.setting_value : false;
  }, [settingsData]);

  const questions = watch('questions');

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'questions',
  });

  // Handle drag end event for reordering questions
  const onDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    // Check if the order has actually changed
    if (sourceIndex === destinationIndex) {
      return;
    }

    // Get the current form values to preserve all field data
    const currentValues = getValues();
    const currentQuestions = [...currentValues.questions];

    // Reorder the questions
    const [movedQuestion] = currentQuestions.splice(sourceIndex, 1);
    currentQuestions.splice(destinationIndex, 0, movedQuestion);

    // Update the order property for each question
    const updatedQuestions = currentQuestions.map((question, index) => ({
      ...question,
      order: index + 1
    }));

    // Update the form state with the reordered questions
    reset({
      ...currentValues,
      questions: updatedQuestions
    });
  };

  const createRequestHandler = async (data: FormValues) => {
    try {

      const response = await createRequest({
        applicationId,
        data: {
          collection_method: data.collection_method,
          referee_count: data.referee_count,
          questions: data.questions,
          notes: data.notes ?? '',
        }
      }).unwrap();

      enqueueSnackbar(t('applications.references.initiate_request_modal.api.success'), { variant: 'success' });
      reset();
      onClose();
    }
    catch (error) {
      enqueueSnackbar(t('applications.references.initiate_request_modal.api.default_error'), { variant: 'error' });
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" >
      <DialogTitle>{t('applications.references.initiate_request_modal.title')}</DialogTitle>

      <DialogContent>
        <FormProvider {...methods}>
          <Stack direction="column" spacing={2}>

            <RHFNumberInput
              name="referee_count"
              label={t('applications.references.initiate_request_modal.labels.referee_count')}
            />

            <Stack direction="column" spacing={.5}>
              <RHFSelect
                name="collection_method"
                label={t('applications.references.initiate_request_modal.labels.collection_method')}
                sx={{ flexGrow: 1 }}
              >
                <MenuItem value={ReferenceCollectionMethod.REFEREE}>{t('applications.references.initiate_request_modal.labels.referee_collection_method')}</MenuItem>
                <MenuItem value={ReferenceCollectionMethod.MANUAL}>{t('applications.references.initiate_request_modal.labels.manual_collection_method')}</MenuItem>
              </RHFSelect>

              {
                collectionMethod === ReferenceCollectionMethod.MANUAL && (
                  <Typography variant="caption" color="text.secondary">
                    {t('applications.references.initiate_request_modal.labels.manual_collection_method_info')}
                  </Typography>
                )
              }

              {
                collectionMethod === ReferenceCollectionMethod.REFEREE && (
                  <Typography variant="caption" color="text.secondary">
                    {t('applications.references.initiate_request_modal.labels.referee_collection_method_info')}
                  </Typography>
                )
              }
            </Stack>

            <RHFTextField
              name="notes"
              label={t('applications.references.initiate_request_modal.labels.candidate_notes')}
              multiline
              rows={4}
            />

            {
              !areQuestionsEditable && (
                <Alert severity="warning" icon={false}>
                  {t('applications.references.initiate_request_modal.editing_disabled_message')}
                </Alert>
              )
            }

            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="questions">
                {(provided) => (
                  <Grid
                    container
                    spacing={2}
                    sx={{ alignItems: 'center' }}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    <Grid item xs={12}>
                      {
                        fields.map((field, index) => (
                          <Draggable
                            key={field.id || `question-${index}`}
                            draggableId={field.id || `question-${index}`}
                            index={index}
                            isDragDisabled={!areQuestionsEditable}
                          >
                            {(draggable, snapshot) => (
                              <Grid
                                container
                                spacing={2}
                                sx={{
                                  alignItems: 'center',
                                  my: 1,
                                  backgroundColor: snapshot.isDragging ? 'rgba(0, 0, 0, 0.05)' : 'inherit',
                                }}
                                key={field.id}
                                ref={draggable.innerRef}
                                {...draggable.draggableProps}
                              >
                                <Grid item xs={11} sm={7}>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box
                                      {...draggable.dragHandleProps}
                                      sx={{
                                        mr: 1,
                                        color: 'text.disabled',
                                        cursor: areQuestionsEditable ? 'grab' : 'default',
                                        display: 'flex',
                                        alignItems: 'center'
                                      }}
                                    >
                                      <DragIndicatorIcon />
                                    </Box>
                                    <RHFTextField
                                      disabled={!areQuestionsEditable}
                                      name={`questions.${index}.question`}
                                      label={t('applications.references.initiate_request_modal.labels.question')}
                                      fullWidth
                                    />
                                  </Box>
                                </Grid>
                                <Grid item xs={11} sm={3}>
                                  <RHFCheckbox
                                    disabled={!areQuestionsEditable}
                                    name={`questions.${index}.required`}
                                    label={t('applications.references.initiate_request_modal.labels.required')}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={1}>
                                  <IconButton sx={{ mt: .5 }} onClick={() => remove(index)} color="error" disabled={!areQuestionsEditable}>
                                    <DeleteIcon />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            )}
                          </Draggable>
                        ))
                      }
                      {provided.placeholder}
                    </Grid>
                  </Grid>
                )}
              </Droppable>
            </DragDropContext>

            {
              errors.questions?.root && (
                <Typography variant="caption" color="error">
                  {errors.questions?.root.message}
                </Typography>
              )
            }

          </Stack>
        </FormProvider>
      </DialogContent>

      <DialogActions>
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" width="100%">
          <Box sx={{ display: 'flex', justifyContent: 'end' }}>

            {
              areQuestionsEditable && (
                <Button startIcon={<Iconify icon="gridicons:add-outline" />} disabled={questions.length > MAX_QUESTIONS} variant="contained" color="primary" onClick={() => append({
                  question: '',
                  required: true,
                  order: questions.length + 1,
                  id: `question-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
                })}>
                  {t('admin-organization.settings.references.labels.add_question')}
                </Button>
              )
            }

          </Box>

          <Stack direction="row" spacing={2}>
            <Button onClick={onClose}>{t('common.close')}</Button>
            <LoadingButton
              type="submit"
              onClick={handleSubmit(createRequestHandler)}
              variant="contained"
              loading={isSubmitting}
              disabled={!isValid}
            >
              {t('applications.references.initiate_request_modal.labels.send_request')}
            </LoadingButton>
          </Stack>
        </Stack>
      </DialogActions>

    </Dialog >
  );
}
